import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Social Login Buttons
 * STRUCTURE READY - Production setup required:
 * 
 * For Google: Add Google Sign-In SDK and Client ID
 *   <script src="https://accounts.google.com/gsi/client" async defer></script>
 *   Use REACT_APP_GOOGLE_CLIENT_ID in .env
 * 
 * For Apple: Add Apple Sign-In JS
 *   https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js
 *   Use REACT_APP_APPLE_CLIENT_ID
 * 
 * For Facebook: Add Facebook SDK
 *   Use REACT_APP_FACEBOOK_APP_ID
 */
const SocialLoginButtons = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSocialLogin = async (provider) => {
    // MOCK FLOW - In production, this should:
    // 1. Open provider OAuth flow
    // 2. Get the ID token / access token from provider
    // 3. Verify on backend and create session
    
    toast.info(`Login com ${provider} - estrutura pronta. Configure as credenciais no .env`);
    
    // EXAMPLE: When ready, replace with real flow like:
    // const idToken = await window.google.accounts.id.prompt();
    // const response = await axios.post(`${API_BASE_URL}/auth/social/login`, {
    //   provider: 'google',
    //   token: idToken,
    //   email: userInfo.email,
    //   full_name: userInfo.name,
    //   provider_user_id: userInfo.sub,
    //   avatar_url: userInfo.picture
    // });
    // localStorage.setItem('token', response.data.access_token);
    // localStorage.setItem('user', JSON.stringify(response.data.user));
    // navigate('/');
  };

  return (
    <div className="space-y-2 mt-4">
      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-white px-2 text-muted-foreground">{t('login.continueWith')}</span>
        </div>
      </div>

      <button
        data-testid="login-google"
        type="button"
        onClick={() => handleSocialLogin('google')}
        className="w-full py-2.5 border border-border rounded-full font-semibold text-sm flex items-center justify-center gap-2 hover:bg-muted transition-colors active:scale-95"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Google
      </button>

      <button
        data-testid="login-apple"
        type="button"
        onClick={() => handleSocialLogin('apple')}
        className="w-full py-2.5 bg-black text-white rounded-full font-semibold text-sm flex items-center justify-center gap-2 hover:bg-black/90 transition-colors active:scale-95"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
        </svg>
        Apple
      </button>

      <button
        data-testid="login-facebook"
        type="button"
        onClick={() => handleSocialLogin('facebook')}
        className="w-full py-2.5 bg-[#1877F2] text-white rounded-full font-semibold text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity active:scale-95"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
        Facebook
      </button>
    </div>
  );
};

export default SocialLoginButtons;
